import { ReactNode, useState } from 'react';
import { Button, ButtonGroup } from '@mui/material';
import classNames from 'classnames';

import './ToggleButtons.scss';

interface Option {
    name: string;
    icon?: ReactNode;
    disabled?: boolean;
}

interface ToggleButtonsProps<T extends Option> {
    // an array of options to toggle between
    options: T[];
    // callback with the selected options name, called whenever a new selection is made
    onChange: (selected: T) => void;
    // the default selection to initialise the toggle buttons, defaults to the first options
    defaultSelection?: T['name'];
    className?: string;
}

export function ToggleButtons<T extends Option>({
    options,
    onChange,
    defaultSelection = options[0]?.name,
    className,
}: ToggleButtonsProps<T>) {
    const [selected, setSelected] = useState<T['name'] | undefined>(defaultSelection);

    if (options.length <= 1) {
        return null;
    }

    return (
        <ButtonGroup className={classNames('ToggleButtons', className)}>
            {options.map((option) => (
                <Button
                    disableRipple
                    key={option.name}
                    className={classNames('ToggleButtons__button', {
                        'ToggleButtons__button--selected': selected === option.name,
                    })}
                    onClick={() => {
                        setSelected(option.name);
                        onChange(option);
                    }}
                    startIcon={option.icon}
                    disabled={option.disabled}
                >
                    {option.name}
                </Button>
            ))}
        </ButtonGroup>
    );
}
